import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Plus,
  Loader2,
  X,
  Pause,
  Play,
  Unplug,
  Settings2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  AlertCircle,
  RefreshCw,
  ArrowRightLeft,
} from "lucide-react";
import { emailConnectionsApi, PaginatedResponse } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import { notifyError } from "../ui/Toast";

// ── Types ────────────────────────────────────────────────────────────

interface EmailConnection {
  _id: string;
  provider: string;
  mailbox: {
    email: string;
    displayName?: string;
  };
  settings: {
    includeFolders: string[];
    excludeFolders: string[];
    syncSent: boolean;
    storeBody: boolean;
    storeAttachments: boolean;
    threadByProviderThreadId: boolean;
  };
  status: {
    state: string;
    lastSyncAt?: string;
    lastErrorAt?: string;
    lastErrorMessage?: string;
  };
  createdAt: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

const stateBadge = (state: string) => {
  switch (state) {
    case "active":
      return { className: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Active" };
    case "paused":
      return { className: "bg-amber-100 text-amber-700", icon: Pause, label: "Paused" };
    case "error":
      return { className: "bg-red-100 text-red-700", icon: AlertCircle, label: "Error" };
    case "disconnected":
      return { className: "bg-slate-100 text-slate-500", icon: Unplug, label: "Disconnected" };
    default:
      return { className: "bg-slate-100 text-slate-600", icon: Clock, label: state };
  }
};

const providerLabel = (provider: string) => {
  switch (provider) {
    case "gmail":
      return "Gmail";
    case "microsoft":
      return "Outlook / Microsoft 365";
    case "zoho_imap":
      return "Zoho Mail";
    case "imap_custom":
      return "Custom IMAP";
    default:
      return provider;
  }
};

const providerColor = (provider: string) => {
  switch (provider) {
    case "gmail":
      return "bg-red-50 text-red-700 border-red-200";
    case "microsoft":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "zoho_imap":
      return "bg-yellow-50 text-yellow-700 border-yellow-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "Never";
  return new Date(dateStr).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ── Component ────────────────────────────────────────────────────────

const EmailConnectionsPage: React.FC = () => {
  const [connections, setConnections] = useState<EmailConnection[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showConnect, setShowConnect] = useState(false);
  const [showSettings, setShowSettings] = useState<EmailConnection | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState<EmailConnection | null>(null);

  // Connect form
  const [connectProvider, setConnectProvider] = useState("");
  const [connectLoading, setConnectLoading] = useState(false);

  // Settings form
  const [editSyncSent, setEditSyncSent] = useState(true);
  const [editStoreBody, setEditStoreBody] = useState(true);
  const [editStoreAttachments, setEditStoreAttachments] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // ── Fetch Connections ─────────────────────────────────────────────
  const fetchConnections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await emailConnectionsApi.list();
      const data = res.data as PaginatedResponse<EmailConnection>;
      setConnections(data.data || []);
    } catch (err) {
      console.error("Failed to fetch email connections:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // ── Connect Flow (OAuth) ─────────────────────────────────────────
  const handleOAuth = async (provider: "gmail" | "microsoft") => {
    setConnectLoading(true);
    try {
      const res = await emailConnectionsApi.getAuthUrl(provider);
      const { authUrl } = res.data;
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (err: any) {
      console.error(`Failed to get ${provider} auth URL:`, err);
      notifyError("Couldn't start the connection. Please try again.", err);
      setConnectLoading(false);
    }
  };

  // ── Pause / Resume ────────────────────────────────────────────────
  const handlePause = async (id: string) => {
    try {
      await emailConnectionsApi.pause(id);
      fetchConnections();
    } catch (err) {
      console.error("Failed to pause:", err);
      notifyError("Couldn't pause syncing for this mailbox.", err);
    }
  };

  const handleResume = async (id: string) => {
    try {
      await emailConnectionsApi.resume(id);
      fetchConnections();
    } catch (err) {
      console.error("Failed to resume:", err);
      notifyError("Couldn't resume syncing for this mailbox.", err);
    }
  };

  // ── Disconnect ────────────────────────────────────────────────────
  const handleDisconnect = async (id: string) => {
    try {
      await emailConnectionsApi.disconnect(id);
      setShowDisconnectConfirm(null);
      fetchConnections();
    } catch (err) {
      console.error("Failed to disconnect:", err);
      notifyError("Couldn't disconnect this mailbox.", err);
    }
  };

  // ── Save Settings ─────────────────────────────────────────────────
  const openSettings = (conn: EmailConnection) => {
    setShowSettings(conn);
    setEditSyncSent(conn.settings.syncSent);
    setEditStoreBody(conn.settings.storeBody);
    setEditStoreAttachments(conn.settings.storeAttachments);
  };

  const handleSaveSettings = async () => {
    if (!showSettings) return;
    setSavingSettings(true);
    try {
      await emailConnectionsApi.update(showSettings._id, {
        settings: {
          syncSent: editSyncSent,
          storeBody: editStoreBody,
          storeAttachments: editStoreAttachments,
        },
      });
      setShowSettings(null);
      fetchConnections();
    } catch (err) {
      console.error("Failed to save settings:", err);
      notifyError("Couldn't save mailbox settings.", err);
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Email Connections</h1>
          <p className="text-sm text-slate-500 mt-1">
            Connect your email accounts to sync messages
          </p>
        </div>
        <button
          onClick={() => setShowConnect(true)}
          className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
          style={{ backgroundColor: "var(--color-primary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
        >
          <Plus className="h-5 w-5" />
          Connect Email
        </button>
      </div>

      {/* Connections List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : connections.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 flex flex-col items-center justify-center text-center">
          <Mail className="h-12 w-12 mb-3 text-slate-300" />
          <p className="font-medium text-lg text-slate-700">No email connections</p>
          <p className="text-sm text-slate-500 mt-1">Connect your Gmail or Outlook account to start syncing emails</p>
          <button
            onClick={() => setShowConnect(true)}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Connect Email
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {connections.map((conn, i) => {
            const badge = stateBadge(conn.status.state);
            const BadgeIcon = badge.icon;
            return (
              <motion.div
                key={conn._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Provider icon */}
                    <div className={`h-12 w-12 rounded-xl flex items-center justify-center border ${providerColor(conn.provider)}`}>
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">{conn.mailbox.email}</h3>
                      {conn.mailbox.displayName && (
                        <p className="text-xs text-slate-500">{conn.mailbox.displayName}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                          <BadgeIcon className="h-3 w-3" />
                          {badge.label}
                        </span>
                        <span className="text-xs text-slate-400">
                          {providerLabel(conn.provider)}
                        </span>
                        <span className="text-xs text-slate-400">
                          Last sync: {formatDate(conn.status.lastSyncAt)}
                        </span>
                      </div>
                      {conn.status.state === "error" && conn.status.lastErrorMessage && (
                        <div className="flex items-start gap-1.5 mt-2 p-2 rounded-lg bg-red-50 border border-red-100">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-red-600">{conn.status.lastErrorMessage}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {conn.status.state === "active" && (
                      <button
                        onClick={() => handlePause(conn._id)}
                        className="p-2 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
                        title="Pause sync"
                      >
                        <Pause className="h-4 w-4" />
                      </button>
                    )}
                    {conn.status.state === "paused" && (
                      <button
                        onClick={() => handleResume(conn._id)}
                        className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title="Resume sync"
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    )}
                    {conn.status.state === "error" && (
                      <button
                        onClick={() => handleResume(conn._id)}
                        className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Retry"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => openSettings(conn)}
                      className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                      title="Settings"
                    >
                      <Settings2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setShowDisconnectConfirm(conn)}
                      className="p-2 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                      title="Disconnect"
                    >
                      <Unplug className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Sync settings summary */}
                <div className="mt-3 pt-3 border-t border-slate-100 flex gap-4 text-xs text-slate-500">
                  <span>Folders: {conn.settings.includeFolders.length > 0 ? conn.settings.includeFolders.join(", ") : "All"}</span>
                  <span>Sync sent: {conn.settings.syncSent ? "Yes" : "No"}</span>
                  <span>Store body: {conn.settings.storeBody ? "Yes" : "No"}</span>
                  <span>Attachments: {conn.settings.storeAttachments ? "Yes" : "No"}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Connect Email Modal ── */}
      <PremiumModal
        open={showConnect}
        onClose={() => setShowConnect(false)}
        title="Connect Email"
        subtitle="Link your email account to sync messages"
        icon={Mail}
        size="sm"
      >
        {/* Provider Selection */}
        {!connectProvider ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 mb-4">Choose your email provider to get started.</p>

            <button
              onClick={() => handleOAuth("gmail")}
              disabled={connectLoading}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-red-300 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                {connectLoading ? (
                  <Loader2 className="h-5 w-5 text-red-600 animate-spin" />
                ) : (
                  <Mail className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">Connect Gmail</p>
                <p className="text-xs text-slate-500">Google Workspace or personal Gmail</p>
              </div>
            </button>

            <button
              onClick={() => handleOAuth("microsoft")}
              disabled={connectLoading}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                {connectLoading ? (
                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                ) : (
                  <Mail className="h-5 w-5 text-blue-600" />
                )}
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">Connect Outlook</p>
                <p className="text-xs text-slate-500">Microsoft 365 or Outlook.com</p>
              </div>
            </button>

            <button
              onClick={() => setConnectProvider("imap_custom")}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors"
            >
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-slate-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">Custom IMAP</p>
                <p className="text-xs text-slate-500">Any IMAP-compatible provider</p>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col items-center text-center py-4">
              <div className="h-14 w-14 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                <Mail className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-base font-bold text-slate-800 mb-1">
                Custom IMAP Integration Coming Soon
              </h3>
              <p className="text-sm text-slate-500 max-w-xs">
                Custom IMAP integration is currently in development. We're working on secure IMAP credential handling for third-party email providers.
              </p>
            </div>

            <a
              href="mailto:support@kavoa.ca?subject=Please%20prioritize%20IMAP%20email%20integration&body=Hi%2C%0A%0AI%20would%20like%20to%20use%20IMAP%20integration%20in%20Kavoa.%20Please%20prioritize%20this%20feature.%0A%0AThanks!"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Click here to pester us to prioritize this
            </a>

            <div className="flex justify-center">
              <button
                onClick={() => setConnectProvider("")}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
            </div>
          </div>
        )}
      </PremiumModal>

      {/* ── Settings Modal ── */}
      <PremiumModal
        open={!!showSettings}
        onClose={() => setShowSettings(null)}
        title="Connection Settings"
        subtitle={showSettings?.mailbox.email}
        icon={Settings2}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowSettings(null)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {savingSettings ? "Saving..." : "Save Settings"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Toggle: Sync Sent */}
          <label className="flex items-center justify-between p-3 rounded-xl bg-slate-50 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-slate-700">Sync sent mail</p>
              <p className="text-xs text-slate-500">Capture emails sent outside the app</p>
            </div>
            <div
              onClick={() => setEditSyncSent(!editSyncSent)}
              className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${editSyncSent ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <div className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition-transform shadow-sm ${editSyncSent ? "translate-x-5" : ""}`} />
            </div>
          </label>

          {/* Toggle: Store Body */}
          <label className="flex items-center justify-between p-3 rounded-xl bg-slate-50 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-slate-700">Store email body</p>
              <p className="text-xs text-slate-500">Save HTML and text content of emails</p>
            </div>
            <div
              onClick={() => setEditStoreBody(!editStoreBody)}
              className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${editStoreBody ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <div className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition-transform shadow-sm ${editStoreBody ? "translate-x-5" : ""}`} />
            </div>
          </label>

          {/* Toggle: Store Attachments */}
          <label className="flex items-center justify-between p-3 rounded-xl bg-slate-50 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-slate-700">Store attachments</p>
              <p className="text-xs text-slate-500">Download and archive email attachments</p>
            </div>
            <div
              onClick={() => setEditStoreAttachments(!editStoreAttachments)}
              className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer ${editStoreAttachments ? "bg-emerald-500" : "bg-slate-300"}`}
            >
              <div className={`absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition-transform shadow-sm ${editStoreAttachments ? "translate-x-5" : ""}`} />
            </div>
          </label>
        </div>
      </PremiumModal>

      {/* ── Disconnect Confirmation ── */}
      <PremiumModal
        open={!!showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(null)}
        title="Disconnect?"
        icon={AlertTriangle}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDisconnectConfirm(null)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={() => showDisconnectConfirm && handleDisconnect(showDisconnectConfirm._id)}
              className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700"
            >
              Disconnect
            </button>
          </div>
        }
      >
        {showDisconnectConfirm && (
          <p className="text-sm text-slate-600">
            Disconnect <strong>{showDisconnectConfirm.mailbox.email}</strong>? Existing synced messages will be retained, but new emails will no longer sync.
          </p>
        )}
      </PremiumModal>
    </div>
  );
};

export default EmailConnectionsPage;
